import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {rgba} from "motion";

const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    advertising: false,
  });
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const storedConsent = localStorage.getItem('cookieConsent');
    if (!storedConsent) {
      // Show banner if no choice has been made
      setVisible(true);
    } else {
      // Load stored preferences
      try {
        const savedPreferences = JSON.parse(storedConsent);
        setPreferences(savedPreferences);

        // Apply preferences
        applyPreferences(savedPreferences);
      } catch (e) {
        console.error('Error parsing stored cookie consent:', e);
        setVisible(true);
      }
    }
  }, []);

  // Apply cookie preferences to the page
  const applyPreferences = (prefs) => {
    // Handle Google Analytics and AdSense consent
    if (window.gtag && window.dataLayer) {
      const consentUpdate = {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.advertising ? 'granted' : 'denied',
        'ad_user_data': prefs.advertising ? 'granted' : 'denied',
        'ad_personalization': prefs.advertising ? 'granted' : 'denied'
      };

      window.gtag('consent', 'update', consentUpdate);

      // Trigger page_view after consent update for analytics
      if (prefs.analytics) {
        window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID);
      }
    }

    // Store consent decision for other scripts that might need it
    window.cookieConsent = {
      analytics: prefs.analytics,
      advertising: prefs.advertising,
      necessary: prefs.necessary
    };
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      advertising: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    applyPreferences(allAccepted);
    setVisible(false);
  };

  const handleRejectAll = () => {
    const allRejected = {
      necessary: true, // Necessary cookies are always accepted
      analytics: false,
      advertising: false,
    };
    setPreferences(allRejected);
    localStorage.setItem('cookieConsent', JSON.stringify(allRejected));
    applyPreferences(allRejected);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    applyPreferences(preferences);
    setVisible(false);
    setShowPreferences(false);
  };

  const handlePreferenceChange = (e) => {
    const { name, checked } = e.target;
    setPreferences(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const togglePreferences = () => {
    setShowPreferences(!showPreferences);
  };

  const reopenConsentBanner = () => {
    setVisible(true);
    setShowPreferences(false);
  };

  // If banner is not visible, render only a small button to reopen it
  if (!visible) {
    return (
      <div
        onClick={reopenConsentBanner}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: '#111827',
          border: '1px solid #ccc',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          zIndex: 1000
        }}
        title="Impostazioni Cookie"
      >
        🍪
      </div>
    );
  }

  // Cookie banner styling
  const bannerStyle = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: '#003333',
    borderTop: '1px solid #ddd',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    padding: '15px 20px',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: showPreferences ? '80vh' : '30vh',
    overflowY: 'auto',
    transition: 'max-height 0.3s ease-in-out'
  };

  const buttonStyle = {
    padding: '8px 15px',
    margin: '5px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'rgba(255, 255, 255, 0.45)',
    color: 'white',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    color: '#333',
    border: '1px solid #ddd'
  };

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    marginTop: '10px'
  };

  return (
    <div style={bannerStyle}>
      <div>
        <h3 style={{ margin: '0 0 10px 0' }}>{t('cookieConsent.title')}</h3>
        <p>
          {t('cookieConsent.description')}
        </p>

        {showPreferences && (
          <div style={{ marginTop: '15px' }}>
            <h4 style={{ marginBottom: '10px' }}>{t('cookieConsent.preferences')}</h4>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={true} // Necessary cookies are always accepted
                  disabled={true} // Necessary cookies cannot be disabled
                  style={{ marginRight: '8px' }}
                />
                {t('cookieConsent.necessaryCookies')}
              </label>
              <p style={{ margin: '5px 0 10px 24px', color: '#666', fontSize: '12px' }}>
                {t('cookieConsent.necessaryCookiesDescription')}
              </p>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="analytics"
                  checked={preferences.analytics}
                  onChange={handlePreferenceChange}
                  style={{ marginRight: '8px' }}
                />
                {t('cookieConsent.analyticsCookies')}
              </label>
              <p style={{ margin: '5px 0 10px 24px', color: '#666', fontSize: '12px' }}>
                {t('cookieConsent.analyticsCookiesDescription')}
              </p>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="advertising"
                  checked={preferences.advertising}
                  onChange={handlePreferenceChange}
                  style={{ marginRight: '8px' }}
                />
                {t('cookieConsent.advertisingCookies')}
              </label>
              <p style={{ margin: '5px 0 10px 24px', color: '#666', fontSize: '12px' }}>
                {t('cookieConsent.advertisingCookiesDescription')}
              </p>
            </div>
          </div>
        )}
      </div>

      <div style={buttonContainerStyle}>
        <button
          onClick={handleRejectAll}
          style={secondaryButtonStyle}
        >
          {t('cookieConsent.rejectAll')}
        </button>
        <button
          onClick={togglePreferences}
          style={secondaryButtonStyle}
        >
          {showPreferences ? t('cookieConsent.hidePreferences') : t('cookieConsent.customize')}
        </button>
        {showPreferences ? (
          <button
            onClick={handleSavePreferences}
            style={primaryButtonStyle}
          >
            {t('cookieConsent.savePreferences')}
          </button>
        ) : (
          <button
            onClick={handleAcceptAll}
            style={primaryButtonStyle}
          >
            {t('cookieConsent.acceptAll')}
          </button>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
